import { useState, useEffect, useMemo } from "react";
import { apiGet, apiPost } from "../lib/apiClient.js";
import { makeTtlCache } from "../lib/forecast/reportCache.js";
import { forecastPointers, fetchForecastSeries } from "../lib/forecast/reportPointer.js";
import { summarizeShiftStability } from "../lib/forecast/shiftStability.js";

// Per-tab TTL cache keyed by row_key — a module singleton so it survives
// open→close→reopen. This is a DEDICATED cache for the plot series, separate from
// useReportPointer's modal-overlay cache: same key space + 5-min TTL, but a
// distinct Map (not shared). The only overlap is the latest pointer's report,
// which each may fetch once — a single duplicate fetch not worth exporting a
// mutable singleton across hook modules to avoid.
const REPORT_TTL_MS = 5 * 60 * 1000;
const _seriesReportCache = makeTtlCache({ ttlMs: REPORT_TTL_MS });

/**
 * Read-through SERIES hook — the plot counterpart to `useReportPointer`. Given a
 * run's `report_pointers`, fetch EVERY `kind:'forecast'` pointer's stored report
 * (in parallel, TTL-cached by `row_key`) and return the resolved ones as plot
 * moments `[{fireTs, p}]` for `ProbabilityPlot` to merge with the manual
 * `history` line (via `buildReforecastMoments`).
 *
 * Best-effort, matching the single-report overlay: a pointer whose report 404s
 * or otherwise fails to fetch, or that carries neither a numeric prediction nor a
 * multi-value distribution, is omitted (`fetchForecastReport` returns null →
 * no moment); a multi-value fire's moment has `p: null`. Returns `[]`
 * until the first fetch resolves and whenever disabled or there are no forecast
 * pointers.
 *
 * v1 fetches ALL forecast pointers with no cap (per design). This is an
 * unbounded hourly series — a long-lived contract accrues many pointers, meaning
 * that many BigTable point-reads on the first open (TTL-cached for 5 min after).
 * A most-recent-N cap or a batch endpoint drops into this hook if it bites.
 *
 * @param {{
 *   reportPointers?: Array,     // the active run's report_pointers
 *   enabled?: boolean,          // gate (forecastTracking && has pointers)
 *   apiGetImpl?: Function,      // injectable for tests
 *   cache?: object              // injectable TTL cache
 * }} args
 * Also returns `shiftStabilitySummary` — the FORECAST STABILITY aggregate over the same N fires
 * this hook already reads (see `lib/forecast/shiftStability.js`). Derived here rather than in
 * `AppInner` for two reasons: the verdicts arrive in the same payload as the plot moments (so
 * computing it elsewhere would mean a second read of the same rows), and an aggregate — not a
 * per-forecast badge — is the only defensible surface for a label the judge flips on ~5% of
 * re-runs at temperature 0. `null` means the empty state: no fire carried a verdict.
 *
 * @returns {{
 *   asyncMoments: Array<{fireTs:string, p:number|null, dist:object|null}>,
 *   shiftStabilitySummary: object|null,
 * }}
 */
export function useReportPointerSeries({
  reportPointers,
  enabled,
  apiGetImpl = apiGet,
  apiPostImpl = apiPost,
  cache = _seriesReportCache,
}) {
  // Keyed state (same shape as useReportPointer's useKeyedReadThrough): the
  // effect only setState()s inside the async resolve — never synchronously in the
  // body — and the returned value is DERIVED on a key match, so a resolved fetch
  // for a superseded pointer set never leaks and disabling clears without a
  // synchronous reset (avoids cascading-render churn).
  const [state, setState] = useState({ key: null, moments: [], verdicts: [] });
  // The forecast pointers to fetch this render. A stable string of their sorted
  // row_keys is the effect dependency + match key, so we re-fetch only when the
  // pointer SET changes — not on every unrelated `active` mutation or re-render.
  // FULL FIDELITY: every scheduled fire is plotted (no downsampling). This is now
  // affordable because fetchForecastMoments primes them in ONE batched round-trip
  // (chunked ≤256 keys server-side), rather than N per-key reads — so the line
  // traces the true hourly trajectory. (Product: Chuck wants full reforecast
  // fidelity, not daily-thinned history.)
  const fps = enabled ? forecastPointers(reportPointers) : [];
  const key = fps
    .map((p) => p.row_key)
    .sort()
    .join("|");
  useEffect(() => {
    if (!enabled || fps.length === 0) return undefined;
    let cancelled = false;
    fetchForecastSeries(fps, { apiGet: apiGetImpl, apiPost: apiPostImpl, cache })
      .then(({ moments, verdicts }) => {
        if (!cancelled) setState({ key, moments, verdicts });
      })
      .catch(() => {
        // fetchForecastSeries is best-effort (Promise.allSettled internally), so
        // this only fires on an unexpected throw (e.g. a misbehaving injected
        // dep); fall back to an empty series rather than leaving stale state —
        // mirrors useReportPointer's useKeyedReadThrough catch.
        if (!cancelled) setState({ key, moments: [], verdicts: [] });
      });
    return () => {
      cancelled = true;
    };
    // `key` captures the pointer set; apiGetImpl + cache are stable singletons.
  }, [enabled, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const matched = enabled && fps.length > 0 && state.key === key;
  const asyncMoments = matched ? state.moments : [];
  // Memoized on the settled verdict array identity — it only changes when a fetch resolves for
  // a matching pointer set, so the summary (and any component keyed on it) is referentially
  // stable across unrelated re-renders.
  const verdicts = matched ? state.verdicts : null;
  const shiftStabilitySummary = useMemo(
    () => (verdicts ? summarizeShiftStability(verdicts) : null),
    [verdicts],
  );
  return { asyncMoments, shiftStabilitySummary };
}
